import React, { useState, useMemo } from 'react';
import { useTickets, MaintenanceTicket, TicketStatus } from '../context/TicketContext';
import { StatusBadge, SeverityBadge, SlaCountdown } from '../components/StatusBadge';
import TicketDrawer from '../components/TicketDrawer';
import './Tickets.css';

const STATUS_TABS: { key: TicketStatus | 'all'; label: string }[] = [
  { key: 'all',         label: 'All'         },
  { key: 'open',        label: 'Open'        },
  { key: 'assigned',    label: 'Assigned'    },
  { key: 'in_progress', label: 'In Progress' },
  { key: 'resolved',    label: 'Resolved'    },
  { key: 'closed',      label: 'Closed'      },
];

const Tickets: React.FC = () => {
  const { tickets, technicians, createTicket, overdueCount } = useTickets();

  const [statusFilter,   setStatusFilter]   = useState<TicketStatus | 'all'>('all');
  const [severityFilter, setSeverityFilter] = useState<'all' | 'critical' | 'warning'>('all');
  const [assigneeFilter, setAssigneeFilter] = useState<string>('all');
  const [search,         setSearch]         = useState('');
  const [selected,       setSelected]       = useState<MaintenanceTicket | null>(null);
  const [showCreate,     setShowCreate]     = useState(false);

  // New ticket form state
  const [newTitle,    setNewTitle]    = useState('');
  const [newMachine,  setNewMachine]  = useState('');
  const [newSeverity, setNewSeverity] = useState<'warning' | 'critical'>('warning');
  const [newDesc,     setNewDesc]     = useState('');

  const filtered = useMemo(() => {
    return tickets.filter(t => {
      if (statusFilter   !== 'all' && t.status   !== statusFilter)   return false;
      if (severityFilter !== 'all' && t.severity !== severityFilter) return false;
      if (assigneeFilter !== 'all' && t.assigneeId !== assigneeFilter) return false;
      if (search && !t.title.toLowerCase().includes(search.toLowerCase()) &&
          !t.machineName.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [tickets, statusFilter, severityFilter, assigneeFilter, search]);

  const counts = useMemo(() => ({
    open:        tickets.filter(t => t.status === 'open').length,
    assigned:    tickets.filter(t => t.status === 'assigned').length,
    in_progress: tickets.filter(t => t.status === 'in_progress').length,
    resolved:    tickets.filter(t => t.status === 'resolved').length,
    resolvedToday: tickets.filter(t => t.status === 'resolved' &&
      t.resolvedAt && Date.now() - t.resolvedAt < 86400_000).length,
  }), [tickets]);

  const handleCreate = () => {
    if (!newTitle.trim() || !newMachine.trim()) return;
    createTicket({
      workspaceId: 'demo', title: newTitle.trim(),
      description: newDesc.trim() || newTitle.trim(),
      severity: newSeverity, status: 'open',
      machineId: 'manual', machineName: newMachine.trim(),
    });
    setNewTitle(''); setNewMachine(''); setNewDesc(''); setNewSeverity('warning');
    setShowCreate(false);
  };

  // Refresh selected ticket from latest state
  const selectedLatest = selected
    ? tickets.find(t => t.ticketId === selected.ticketId) ?? null
    : null;

  return (
    <div className="tickets-page">

      {/* Header */}
      <div className="page-header">
        <div>
          <h1>🎫 Maintenance Tickets</h1>
          <p className="page-subtitle">Track and resolve operational issues across your factory floor</p>
        </div>
        <button className="btn-primary" onClick={() => setShowCreate(s => !s)}>
          + New Ticket
        </button>
      </div>

      {/* Create form */}
      {showCreate && (
        <div className="card create-ticket-form">
          <h3>Create Ticket</h3>
          <div className="ctf-grid">
            <input className="form-input" placeholder="Title *" value={newTitle} onChange={e => setNewTitle(e.target.value)} />
            <input className="form-input" placeholder="Machine name *" value={newMachine} onChange={e => setNewMachine(e.target.value)} />
            <select className="form-input" value={newSeverity} onChange={e => setNewSeverity(e.target.value as any)}>
              <option value="warning">⚠️ Warning</option>
              <option value="critical">🔴 Critical</option>
            </select>
            <input className="form-input" placeholder="Description (optional)" value={newDesc} onChange={e => setNewDesc(e.target.value)} />
          </div>
          <div className="ctf-actions">
            <button className="btn-primary" onClick={handleCreate} disabled={!newTitle.trim() || !newMachine.trim()}>
              Create
            </button>
            <button className="btn-secondary" onClick={() => setShowCreate(false)}>Cancel</button>
          </div>
        </div>
      )}

      {/* KPI bar */}
      <div className="ticket-kpis">
        {[
          { label: 'Open',           value: counts.open,          color: '#f87171', icon: '🔴' },
          { label: 'Assigned',       value: counts.assigned,      color: '#fbbf24', icon: '🟡' },
          { label: 'In Progress',    value: counts.in_progress,   color: '#60a5fa', icon: '🔵' },
          { label: 'Overdue',        value: overdueCount,         color: '#f97316', icon: '⚠️' },
          { label: 'Resolved Today', value: counts.resolvedToday, color: '#34d399', icon: '✅' },
        ].map((k, i) => (
          <div className="ticket-kpi card" key={i}>
            <span className="tkpi-icon">{k.icon}</span>
            <span className="tkpi-value" style={{ color: k.color }}>{k.value}</span>
            <span className="tkpi-label">{k.label}</span>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="ticket-filters card">
        <div className="status-tabs">
          {STATUS_TABS.map(tab => (
            <button
              key={tab.key}
              className={`status-tab ${statusFilter === tab.key ? 'active' : ''}`}
              onClick={() => setStatusFilter(tab.key)}
            >
              {tab.label}
              <span className="tab-count">
                {tab.key === 'all' ? tickets.length : tickets.filter(t => t.status === tab.key).length}
              </span>
            </button>
          ))}
        </div>
        <div className="filter-row">
          <input
            className="search-input"
            placeholder="🔍 Search tickets…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <select className="filter-select" value={severityFilter} onChange={e => setSeverityFilter(e.target.value as any)}>
            <option value="all">All Severities</option>
            <option value="critical">🔴 Critical</option>
            <option value="warning">🟡 Warning</option>
          </select>
          <select className="filter-select" value={assigneeFilter} onChange={e => setAssigneeFilter(e.target.value)}>
            <option value="all">All Technicians</option>
            <option value="">Unassigned</option>
            {technicians.map(t => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Ticket table */}
      <div className="card ticket-table-wrap">
        {filtered.length === 0 ? (
          <div className="empty-tickets">
            <span>🎫</span>
            <p>No tickets match your filters</p>
          </div>
        ) : (
          <table className="ticket-table">
            <thead>
              <tr>
                <th>Ticket</th>
                <th>Machine</th>
                <th>Severity</th>
                <th>Status</th>
                <th>Assignee</th>
                <th>SLA</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(t => (
                <tr key={t.ticketId} className="ticket-row" onClick={() => setSelected(t)}>
                  <td>
                    <div className="ticket-title-cell">
                      <span className="ticket-id">#{t.ticketId.split('_')[1]}</span>
                      <span className="ticket-title-text">{t.title}</span>
                    </div>
                  </td>
                  <td><span className="machine-chip">🏭 {t.machineName}</span></td>
                  <td><SeverityBadge severity={t.severity} size="sm" /></td>
                  <td><StatusBadge   status={t.status}   size="sm" /></td>
                  <td>
                    {t.assigneeName
                      ? <span className="assignee-chip">{t.assigneeName}</span>
                      : <span className="unassigned">— Unassigned</span>}
                  </td>
                  <td><SlaCountdown deadline={t.slaDeadline} resolved={['resolved','closed'].includes(t.status)} /></td>
                  <td className="created-cell">{new Date(t.createdAt).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <TicketDrawer ticket={selectedLatest} onClose={() => setSelected(null)} />
    </div>
  );
};

export default Tickets;
